import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { isFeatureEnabled } from "@shared-core/config/features";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";
import type { EstimateListOptions } from "@shared-core/data/adapter";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { formatCurrencyGbp, formatCount } from "@shared-core/domain/formatters";
import {
  fetchCustomerSuggestions,
  fetchEstimateSuggestions,
  gatherSuggestions,
  type SearchSuggestion,
} from "@shared-core/domain/searchSuggestions";
import { EstimateCard } from "../app/components/domain/orders/EstimateCard";
import { SearchTypeahead } from "../app/components/patterns/SearchTypeahead";
import {
  DateRangePicker,
  type DateRangePreset,
} from "../app/components/patterns/DateRangePicker";
import { EstimateDetailModal } from "../app/components/domain/orders/EstimateDetailModal";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Card } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import { FileText, Filter, ArrowUpDown, Gift, Plus } from "lucide-react";
import type { EstimateViewItem } from "@shared-core/data/api-types";
import type { DateRange } from "react-day-picker";
import { subDays, format } from "date-fns";
import { currentTime } from "@shared-core/domain/current-time";

// EstimatesPage follows the canonical list-endpoint pattern (see
// docs/list-endpoints.md): the backend does filter/sort/paginate.
// Date range + status tab + sort + page are component state; a single
// useEffect refetches on change. Tab counts come from parallel
// count-only queries so the header stays accurate. The "Merchandise"
// filter (`?origin=draft`) lives in the URL like the status, so a link can
// land on it.

type SortOption =
  | "date-desc"
  | "date-asc"
  | "value-high"
  | "value-low"
  | "contribution-high"
  | "contribution-low";
type StatusTab = "all" | "open" | "won" | "lost" | "superseded";

const PAGE_SIZE = 25;

const SORT_OPTION_TO_PARAM: Record<SortOption, string> = {
  "date-desc": "-estimate_date",
  "date-asc": "estimate_date",
  "value-high": "-estimate_amt",
  "value-low": "estimate_amt",
  "contribution-high": "-contribution_amt",
  "contribution-low": "contribution_amt",
};

function toIsoDate(d: Date | undefined): string | undefined {
  return d ? format(d, "yyyy-MM-dd") : undefined;
}

export function EstimatesPage() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") || undefined;
  const urlStatus = searchParams.get("status");
  const initialStatus: StatusTab =
    urlStatus === "all" ||
    urlStatus === "open" ||
    urlStatus === "won" ||
    urlStatus === "lost" ||
    urlStatus === "superseded"
      ? urlStatus
      : "open";
  // Merchandise requests only — offered where the feature is on, so the URL
  // param is inert (and the control absent) where it is off.
  const draftsFeatureEnabled = isFeatureEnabled("merchandise_draft_estimates");
  const draftsOnly =
    draftsFeatureEnabled && searchParams.get("origin") === "draft";

  const [estimates, setEstimates] = useState<EstimateViewItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [filter, setFilter] = useState<StatusTab>(initialStatus);

  // Derive customer display name from first loaded estimate when scoped.
  const customerName = customerFilter
    ? (estimates.find((e) => e.customer_id === customerFilter)?.customer
        ?.name ?? null)
    : null;

  const [selectedEstimate, setSelectedEstimate] =
    useState<EstimateViewItem | null>(null);

  const [datePreset, setDatePreset] = useState<DateRangePreset>("30d");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => ({
    from: subDays(currentTime(), 30),
    to: currentTime(),
  }));

  const [sortBy, setSortBy] = useState<SortOption>("date-desc");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Typeahead suggestions: customers + estimates. A customer scopes the list
  // to that customer (clearing any typed text); an estimate jumps to its
  // detail page. Typed text alone still drives the live substring filter.
  const fetchSuggestions = useCallback(
    (q: string) =>
      gatherSuggestions(
        [fetchCustomerSuggestions, fetchEstimateSuggestions],
        adapter,
        q,
      ),
    [adapter],
  );

  const selectSuggestion = useCallback(
    (s: SearchSuggestion) => {
      if (s.group === "Customers") {
        setSearchQuery("");
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("customer", s.id);
            next.set("page", "1");
            return next;
          },
          { replace: true },
        );
        return;
      }
      navigate(s.href);
    },
    [navigate, setSearchParams],
  );

  // Tab counts + page value. Populated from parallel queries.
  const [counts, setCounts] = useState({ all: 0, open: 0, won: 0, lost: 0 });
  const [estimateValue, setEstimateValue] = useState(0);

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("page", String(p));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const dateRangeOptions: Pick<
    EstimateListOptions,
    "datedAfter" | "datedBefore"
  > = useMemo(
    () => ({
      datedAfter: toIsoDate(dateRange?.from),
      datedBefore: toIsoDate(dateRange?.to),
    }),
    [dateRange],
  );

  const toggleDraftsOnly = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (draftsOnly) next.delete("origin");
        else next.set("origin", "draft");
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  };

  // Reset to page 1 when filter/sort/search/date changes.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sortBy, debouncedSearch, dateRangeOptions, draftsOnly]);

  useEffect(() => {
    let cancelled = false;
    async function loadEstimates() {
      // The drafts filter ignores the date range: a draft is dated when it
      // was raised, and an open one must not drop out of view because it is
      // older than the window. The server filters on `origin=draft`.
      const base: EstimateListOptions = draftsOnly
        ? { origin: "draft" }
        : { ...dateRangeOptions };
      base.sort = SORT_OPTION_TO_PARAM[sortBy];
      base.page = page;
      base.pageSize = PAGE_SIZE;
      if (debouncedSearch.trim()) base.q = debouncedSearch.trim();
      if (customerFilter) base.customerId = customerFilter;
      const itemsOptions: EstimateListOptions = { ...base };
      if (filter !== "all") itemsOptions.status = filter;

      try {
        const [itemsResp, openResp, wonResp, lostResp] = await Promise.all([
          adapter.getEstimates(itemsOptions),
          adapter.getEstimates({ ...base, status: "open", pageSize: 1 }),
          adapter.getEstimates({ ...base, status: "won", pageSize: 1 }),
          adapter.getEstimates({ ...base, status: "lost", pageSize: 1 }),
        ]);
        if (cancelled) return;
        setEstimates(itemsResp.items);
        setTotalPages(itemsResp.totalPages);
        const total = openResp.total + wonResp.total + lostResp.total;
        setCounts({
          all: total,
          open: openResp.total,
          won: wonResp.total,
          lost: lostResp.total,
        });
        setEstimateValue(itemsResp.totalEstimateAmt ?? 0);
      } catch (error) {
        console.error("Failed to load estimates:", error);
      } finally {
        if (!cancelled) {
          setFirstLoad(false);
        }
      }
    }
    loadEstimates();
    return () => {
      cancelled = true;
    };
  }, [
    adapter,
    dateRangeOptions,
    filter,
    sortBy,
    debouncedSearch,
    customerFilter,
    page,
    draftsOnly,
  ]);

  if (firstLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading estimates...</div>
      </div>
    );
  }

  const handleDismissCustomerScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1>
            {customerFilter
              ? `Estimates for ${customerName ?? "…"}`
              : "Estimates"}
          </h1>
          <div className="flex items-center gap-3">
            {/* Estimates are visible wherever estimate DATA is (`estimates_view`);
                the only thing raised from here is a merchandise request. Where
                that is held back the affordance goes with it rather than
                becoming a dead-end button. */}
            {isRouteEnabled("createEstimate") && draftsFeatureEnabled && (
              <Link to={paths.createEstimate()}>
                <Button size="sm">
                  <Plus className="size-4 mr-1" />
                  New merchandise request
                </Button>
              </Link>
            )}
          </div>
        </div>
        {customerFilter && (
          <CustomerScopeChip
            customerName={customerName}
            onDismiss={handleDismissCustomerScope}
          />
        )}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <SearchTypeahead
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder="Search by customer name or description..."
            ariaLabel="Search estimates"
            fetchSuggestions={fetchSuggestions}
            onSelectSuggestion={selectSuggestion}
            className="flex-1 max-w-md"
          />
          {draftsOnly ? (
            // Said in the date range's own place, so nobody reads a date
            // window into a list that is not applying one.
            <p className="w-full sm:w-[280px] text-sm text-muted-foreground">
              All dates — the date range is off while showing merchandise
              estimates, so an older open one is never missed.
            </p>
          ) : (
            <DateRangePicker
              date={dateRange}
              onDateChange={setDateRange}
              preset={datePreset}
              onPresetChange={setDatePreset}
              className="w-full sm:w-[280px]"
            />
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">
            Total Estimates
          </div>
          <div className="text-2xl font-semibold">
            {formatCount(counts.all)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Open</div>
          <div className="text-2xl font-semibold">
            {formatCount(counts.open)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Won</div>
          <div className="text-2xl font-semibold text-[color:var(--color-success)]">
            {formatCount(counts.won)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">
            Estimate Value
          </div>
          <div className="text-2xl font-semibold">
            {formatCurrencyGbp(estimateValue)}
          </div>
        </Card>
      </div>

      {/* Filters and Sort */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div className="flex gap-2 flex-wrap">
            {(["all", "open", "won", "lost"] as const).map((tab) => {
              const count =
                tab === "all"
                  ? counts.all
                  : tab === "open"
                    ? counts.open
                    : tab === "won"
                      ? counts.won
                      : counts.lost;
              return (
                <button
                  key={tab}
                  onClick={() => setFilter(tab)}
                  className={`px-3 py-1 rounded text-sm ${
                    filter === tab
                      ? "bg-[color:var(--color-primary)] text-white"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)} (
                  {formatCount(count)})
                </button>
              );
            })}
          </div>
          {draftsFeatureEnabled && (
            <button
              type="button"
              aria-pressed={draftsOnly}
              onClick={toggleDraftsOnly}
              title="Only merchandise requests — awaiting a quote, quoted or withdrawn — of any date"
              className={`flex items-center gap-1.5 px-3 py-1 rounded text-sm border ${
                draftsOnly
                  ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-white"
                  : "border-border bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              <Gift className="size-3.5" aria-hidden />
              Merchandise
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <ArrowUpDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground hidden sm:inline">
            Sort by:
          </span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="flex-1 sm:flex-initial px-3 py-1 rounded border border-border bg-card text-sm"
          >
            <option value="date-desc">Date (newest first)</option>
            <option value="date-asc">Date (oldest first)</option>
            <option value="value-high">Sales Value (highest)</option>
            <option value="value-low">Sales Value (lowest)</option>
            <option value="contribution-high">Contribution (highest)</option>
            <option value="contribution-low">Contribution (lowest)</option>
          </select>
        </div>
      </div>

      {/* Estimates List */}
      {estimates.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="mb-2">No estimates found</h3>
          <p className="text-muted-foreground">
            {draftsOnly
              ? filter === "all"
                ? "No merchandise requests"
                : `No ${filter} merchandise requests`
              : filter === "all"
                ? "No estimates available"
                : `No ${filter} estimates found`}
          </p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {estimates.map((estimate) => (
            <EstimateCard
              key={estimate.id}
              estimate={estimate}
              onClick={() => setSelectedEstimate(estimate)}
            />
          ))}
        </div>
      )}

      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      {/* Estimate Detail Modal */}
      {selectedEstimate && (
        <EstimateDetailModal
          estimate={selectedEstimate}
          onClose={() => setSelectedEstimate(null)}
        />
      )}
    </div>
  );
}
